import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { api } from '../../utils/api';
import TagService from '../Tags/TagService';
import {
  Upload,
  Link as LinkIcon,
  File as FileIcon,
  Image as ImageIcon,
  ExternalLink,
  Download,
  Edit2,
  Trash2,
  X,
  Globe,
  Calendar,
  User,
  Paperclip,
  RefreshCw,
  CheckCircle,
  FileText,
} from 'lucide-react';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];

const ENTITY_TYPE_ALIASES = {
  customer: 'Customer',
  customers: 'Customer',
  lead: 'Lead',
  leads: 'Lead',
  opportunity: 'Opportunity',
  opportunities: 'Opportunity',
  salesnote: 'SalesNote',
  salesnotes: 'SalesNote',
  'sales-note': 'SalesNote',
  'sales_notes': 'SalesNote',
  activity: 'Activity',
  activities: 'Activity',
  'support-ticket': 'SupportTicket',
  supportticket: 'SupportTicket',
  supporttickets: 'SupportTicket',
  global: 'Global',
};

const canonicalizeEntityTypeValue = (value) => {
  if (!value) return '';
  const key = value.toString().trim();
  if (!key) return '';
  const normalized = key.toLowerCase();
  return ENTITY_TYPE_ALIASES[normalized] || key;
};

function formatBytes(bytes) {
  const numeric = Number(bytes);
  if (!numeric || Number.isNaN(numeric)) return '0 B';
  if (numeric === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.floor(Math.log(numeric) / Math.log(1024));
  const value = numeric / 1024 ** idx;
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[idx]}`;
}

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function isImageAttachment(attachment) {
  if (!attachment) return false;
  if (attachment.AttachmentType === 'Image') return true;
  const fileName = attachment.OriginalFileName || '';
  return IMAGE_EXTENSIONS.some((ext) => fileName.toLowerCase().endsWith(ext));
}

function getFileIcon(attachment) {
  if (!attachment) return <FileIcon className="h-5 w-5 text-gray-400" />;
  if (attachment.AttachmentType === 'URL') {
    return <Globe className="h-5 w-5 text-blue-500" />;
  }
  if (isImageAttachment(attachment)) {
    return <ImageIcon className="h-5 w-5 text-green-500" />;
  }
  return <FileIcon className="h-5 w-5 text-gray-500" />;
}

/**
 * AttachmentService - Displays and manages attachments for a specific entity
 * Designed to match the card list layout from the reference image
 */
const AttachmentService = ({ entityType, entityId, onAttachmentChange }) => {
  const { t } = useTranslation();
  const safeT = (key, fallback) => t(key, fallback);
  const { isDark } = useTheme();
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const fileDialogInProgressRef = useRef(false);

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [textContentModalOpen, setTextContentModalOpen] = useState(false);
  const [viewingTextContent, setViewingTextContent] = useState(null);

  // Use a ref to control modal state - this won't be affected by re-renders
  const modalStateRef = useRef({
    uploadModalOpen: false,
    urlModalOpen: false,
    editModalOpen: false
  });
  
  const [, forceUpdate] = useState({});
  const triggerRerender = useCallback(() => forceUpdate({}), []);

  const openUploadModal = useCallback(() => {
    modalStateRef.current.uploadModalOpen = true;
    triggerRerender();
  }, [triggerRerender]);

  const openTextContentModal = useCallback((attachment) => {
    setViewingTextContent(attachment);
    setTextContentModalOpen(true);
  }, []);

  const closeUploadModal = useCallback(() => {
    modalStateRef.current.uploadModalOpen = false;
    setUploadForm({ file: null, title: '', description: '', tags: '' });
    triggerRerender();
  }, [triggerRerender]);

  const uploadModalOpen = modalStateRef.current.uploadModalOpen;
  const urlModalOpen = modalStateRef.current.urlModalOpen;
  const editModalOpen = modalStateRef.current.editModalOpen;

  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    tags: '',
  });

  const [urlForm, setUrlForm] = useState({
    url: '',
    title: '',
    description: '',
    tags: '',
  });

  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    tags: '',
  });

  const [editingAttachment, setEditingAttachment] = useState(null);

  const canonicalEntityType = useMemo(
    () => canonicalizeEntityTypeValue(entityType),
    [entityType]
  );

  const resolvedEntityId = useMemo(() => {
    if (entityId === null || entityId === undefined) return '';
    const idString = entityId.toString().trim();
    return idString;
  }, [entityId]);


  const loadAttachments = async () => {
    if (!canonicalEntityType || !resolvedEntityId) {
      setAttachments([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = `/attachments/${canonicalEntityType}/${resolvedEntityId}`;
      const data = await api.get(endpoint);
      const attachmentList = data.attachments || data || [];
      setAttachments(attachmentList);
    } catch (err) {
      console.error('Failed to load attachments:', err);
      setError(err.message || 'Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttachments();
  }, [canonicalEntityType, resolvedEntityId]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;

    const file = files[0];
    setUploadForm({
      file,
      title: file.name,
      description: '',
      tags: '',
    });
    openUploadModal();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadForm({
      file,
      title: file.name,
      description: '',
      tags: '',
    });

    if (!uploadModalOpen) {
      openUploadModal();
    }

    if (e.target) {
      e.target.value = '';
    }
  };

  const handleFileUpload = async () => {
    if (!uploadForm.file) {
      setError('Please select a file to upload.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title || uploadForm.file.name);
      formData.append('description', uploadForm.description || '');
      formData.append('tags', uploadForm.tags || '');

    if (!canonicalEntityType || !resolvedEntityId) {
      setError('Invalid entity information for attachment upload.');
      return;
    }

    const uploadEndpoint = `/attachments/${canonicalEntityType}/${resolvedEntityId}/upload`;

      const response = await fetch(`${api.baseURL}/api${uploadEndpoint}`, {
        method: 'POST',
        headers: (() => {
          const headers = api.getAuthHeaders();
          delete headers['Content-Type'];
          return headers;
        })(),
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = 'Failed to upload attachment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      closeUploadModal();
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message || 'Failed to upload attachment');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlSubmit = async () => {
    if (!urlForm.url) {
      setError('Please provide a valid URL.');
      return;
    }

    try {
      new URL(urlForm.url);
    } catch {
      setError('URL format is invalid. Example: https://example.com/file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!canonicalEntityType || !resolvedEntityId) {
        setError('Invalid entity information for attachment link.');
        return;
      }

      const response = await api.post(`/attachments/${canonicalEntityType}/${resolvedEntityId}/url`, {
        url: urlForm.url,
        title: urlForm.title,
        description: urlForm.description,
        tags: urlForm.tags,
      });

      if (response.error) {
        throw new Error(response.error);
      }

      modalStateRef.current.urlModalOpen = false;
      setUrlForm({
        url: '',
        title: '',
        description: '',
        tags: '',
      });
      triggerRerender();
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Failed to add URL attachment:', err);
      setError(err.message || 'Failed to add URL attachment');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (attachment) => {
    setEditingAttachment(attachment);
    setEditForm({
      title: attachment.Title || '',
      description: attachment.Description || '',
      tags: Array.isArray(attachment.Tags) ? attachment.Tags.join(', ') : (attachment.Tags || ''),
    });
    modalStateRef.current.editModalOpen = true;
    triggerRerender();
  };

  const handleEditSave = async () => {
    if (!editingAttachment) return;

    if (!canonicalEntityType || !resolvedEntityId) {
      setError('Invalid entity information for attachment edit.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${api.baseURL}/api/attachments/${editingAttachment.AttachmentID}`,
        {
          method: 'PUT',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(editForm),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to update attachment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      modalStateRef.current.editModalOpen = false;
      setEditingAttachment(null);
      triggerRerender();
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Failed to save attachment:', err);
      setError(err.message || 'Failed to update attachment');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (attachment) => {
    if (!attachment) return;

    const confirmed = window.confirm('Are you sure you want to delete this attachment?');
    if (!confirmed) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${api.baseURL}/api/attachments/${attachment.AttachmentID}`,
        {
          method: 'DELETE',
          headers: api.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to delete attachment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      setError(err.message || 'Failed to delete attachment');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (attachment) => {
    if (!attachment) return;

    try {
      const response = await fetch(
        `${api.baseURL}/api/attachments/download/${attachment.AttachmentID}`,
        {
          headers: api.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download attachment');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.OriginalFileName || attachment.Title || 'file';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError(err.message || 'Failed to download attachment');
    }
  };

  const openPreview = async (attachment) => {
    if (!attachment) return;

    if (attachment.AttachmentType === 'URL' && attachment.URL) {
      window.open(attachment.URL, '_blank', 'noopener');
      return;
    }

    try {
      const response = await fetch(
        `${api.baseURL}/api/attachments/view/${attachment.AttachmentID}`,
        {
          headers: api.getAuthHeaders(),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to open attachment');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (err) {
      console.error('Preview failed:', err);
      setError(err.message || 'Unable to open attachment preview');
    }
  };

  const cardBackground = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const subText = isDark ? 'text-gray-300' : 'text-gray-600';

  return (
    <div
      ref={dropZoneRef}
      className="space-y-4"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Header with Upload/Link buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          <h3 className={`text-base font-medium ${textPrimary}`}>
            <span className="text-xs sm:text-sm md:text-base">{safeT('attachments.title', 'Attachments')}</span> ({attachments.length})
          </h3>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openUploadModal();
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-blue-700"
          >
            <Upload className="h-4 w-4" />
            <span className="text-xs sm:text-sm">{safeT('attachments.uploadFile', 'Upload File')}</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              modalStateRef.current.urlModalOpen = true;
              triggerRerender();
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-green-700"
          >
            <LinkIcon className="h-4 w-4" />
            <span className="text-xs sm:text-sm">{safeT('attachments.addLink', 'Add URL')}</span>
          </button>
        </div>
      </div>

      {/* Drag and drop zone */}
      {isDragOver && (
        <div className="rounded-lg border-2 border-dashed border-blue-500 bg-blue-50 dark:bg-blue-500/10 p-8 text-center">
          <Upload className="h-12 w-12 text-blue-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
            <span className="text-xs sm:text-sm">{safeT('attachments.dropFileHere', 'Drag and drop files here to add more attachments')}</span>
          </p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-200">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-blue-600">
            <span className="text-xs sm:text-sm">{safeT('attachments.loading', 'Loading...')}</span>
          </span>
        </div>
      )}

      {/* Attachment list */}
      {!loading && attachments.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
          <Paperclip className="h-10 w-10 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <span className="text-xs sm:text-sm">{safeT('attachments.noAttachments', 'No attachments yet. Upload a file or add a link to get started.')}</span>
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {attachments.map((attachment) => (
            <div
              key={attachment.AttachmentID}
              className={`rounded-lg border p-4 transition-all duration-150 hover:shadow-md ${
                isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* File Icon */}
                <div className="flex-shrink-0 mt-1">{getFileIcon(attachment)}</div>

                {/* Main Content */}
                <div className="flex-1 min-w-0">
                  {/* Title */}
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      type="button"
                      onClick={() => openPreview(attachment)}
                      className={`text-left text-base font-semibold hover:underline ${
                        isDark ? 'text-blue-300' : 'text-blue-600'
                      }`}
                      title={attachment.Title || attachment.OriginalFileName || 'Untitled'}
                    >
                      {attachment.Title || attachment.OriginalFileName || 'Untitled'}
                    </button>
                    {attachment.TextContent && attachment.TextContent.trim() && (
                      <button
                        type="button"
                        onClick={() => openTextContentModal(attachment)}
                        className="inline-flex items-center text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                        title={safeT('attachments.viewTextContent', 'View extracted text')}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Description */}
                  {attachment.Description && (
                    <p className={`text-sm mb-2 ${subText}`}>
                      {attachment.Description}
                    </p>
                  )}

                  {/* URL Link (if applicable) */}
                  {attachment.AttachmentType === 'URL' && (attachment.FilePath || attachment.URL) && (
                    <a
                      href={attachment.FilePath || attachment.URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1 text-sm mb-2 ${
                        isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'
                      }`}
                    >
                      <ExternalLink className="h-3 w-3" />
                      {attachment.FilePath || attachment.URL}
                    </a>
                  )}

                  {/* Metadata Row */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-2">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {attachment.UploadedByName || 'Unknown'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(attachment.CreatedAt)}
                    </span>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-gray-700 dark:text-gray-300">
                      {formatBytes(attachment.FileSize)}
                    </span>
                  </div>

                  {/* Tags */}
                  <div>
                    <TagService
                      entityType="Attachment"
                      entityId={attachment.AttachmentID}
                      initialTags={
                        Array.isArray(attachment.Tags)
                          ? attachment.Tags
                          : typeof attachment.Tags === 'string'
                          ? attachment.Tags.split(',').map((t) => t.trim()).filter(Boolean)
                          : []
                      }
                      maxTags={10}
                      onChange={() => {
                        loadAttachments();
                      }}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => handleDownload(attachment)}
                    className="rounded-md bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    title={safeT('common.download', 'Download')}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditModal(attachment)}
                    className="rounded-md bg-blue-100 p-2 text-blue-600 hover:bg-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/30"
                    title={safeT('common.edit', 'Edit')}
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(attachment)}
                    className="rounded-md bg-red-100 p-2 text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-300 dark:hover:bg-red-500/30"
                    title={safeT('common.delete', 'Delete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Text Content View Modal */}
      {textContentModalOpen && viewingTextContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-3xl rounded-2xl border ${cardBackground} p-6 shadow-xl max-h-[90vh] flex flex-col`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className={`text-lg font-semibold ${textPrimary}`}>
                  {safeT('attachments.extractedText', 'Extracted Text Content')}
                </h2>
                <p className={`text-sm ${subText} mt-1`}>
                  {viewingTextContent.OriginalFileName || viewingTextContent.FileName || 'Attachment'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setTextContentModalOpen(false);
                  setViewingTextContent(null);
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {viewingTextContent.TextContent ? (
                <div className={`rounded-lg border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                  <pre className={`text-sm font-mono whitespace-pre-wrap break-words ${textPrimary}`}>
                    {viewingTextContent.TextContent}
                  </pre>
                  <div className={`mt-4 text-xs ${subText}`}>
                    {viewingTextContent.TextContent.length.toLocaleString()} characters
                  </div>
                </div>
              ) : (
                <div className={`rounded-lg border p-8 text-center ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className={subText}>
                    {safeT('attachments.noTextContent', 'No text content extracted yet.')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal - Using Portal to prevent parent re-render issues */}
      {uploadModalOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onMouseDown={(e) => {
            e.preventDefault();
            if (e.target === e.currentTarget && !fileDialogInProgressRef.current) {
              closeUploadModal();
            }
          }}
        >
          <div
            className={`w-full max-w-lg rounded-2xl border ${cardBackground} p-6 shadow-xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className={`text-xl font-semibold ${textPrimary}`}>
                  <span className="text-xs sm:text-sm">{safeT('attachments.uploadFile', 'Upload File')}</span>
                </h2>
                <p className={`text-sm ${subText}`}>
                  <span className="text-xs sm:text-sm">{safeT('attachments.uploadDescription', 'Attach a document or image')}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeUploadModal();
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.file', 'File')}</span></label>
                <div
                  className={`mt-2 flex items-center justify-between rounded-lg border px-3 py-2 ${
                    isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <span className="text-sm truncate">
                    {uploadForm.file?.name || safeT('attachments.noFileSelected', 'No file selected')}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      fileDialogInProgressRef.current = true;
                      setTimeout(() => {
                        fileDialogInProgressRef.current = false;
                      }, 2000);
                      fileInputRef.current?.click();
                    }}
                    className="ml-2 rounded-md bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
                  >
                    <span className="text-xs sm:text-sm">{safeT('attachments.browse', 'Browse')}</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.title', 'Title')}</span></label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm((prev) => ({ ...prev, title: e.target.value }))}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.description', 'Description')}</span></label>
                <textarea
                  rows={3}
                  value={uploadForm.description}
                  onChange={(e) =>
                    setUploadForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.tags', 'Tags')}</span></label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm((prev) => ({ ...prev, tags: e.target.value }))}
                  placeholder={safeT('attachments.tagsPlaceholder', 'Comma separated')}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                      : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeUploadModal();
                }}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="text-xs sm:text-sm">{safeT('common.cancel', 'Cancel')}</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFileUpload();
                }}
                disabled={!uploadForm.file || loading}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading && <RefreshCw className="h-4 w-4 animate-spin" />}
                <span className="text-xs sm:text-sm">{loading ? safeT('attachments.uploading', 'Uploading...') : safeT('attachments.upload', 'Upload')}</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* URL Modal */}
      {urlModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-lg rounded-2xl border ${cardBackground} p-6 shadow-xl`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className={`text-xl font-semibold ${textPrimary}`}>
                  <span className="text-xs sm:text-sm">{safeT('attachments.addLink', 'Add Link')}</span>
                </h2>
                <p className={`text-sm ${subText}`}>
                  <span className="text-xs sm:text-sm">{safeT('attachments.addLinkDescription', 'Reference an external resource')}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  modalStateRef.current.urlModalOpen = false;
                  triggerRerender();
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">URL</label>
                <input
                  type="url"
                  value={urlForm.url}
                  onChange={(e) => setUrlForm((prev) => ({ ...prev, url: e.target.value }))}
                  placeholder="https://example.com/document"
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                      : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.title', 'Title')}</span></label>
                <input
                  type="text"
                  value={urlForm.title}
                  onChange={(e) => setUrlForm((prev) => ({ ...prev, title: e.target.value }))}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.description', 'Description')}</span></label>
                <textarea
                  rows={3}
                  value={urlForm.description}
                  onChange={(e) => setUrlForm((prev) => ({ ...prev, description: e.target.value }))}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.tags', 'Tags')}</span></label>
                <input
                  type="text"
                  value={urlForm.tags}
                  onChange={(e) => setUrlForm((prev) => ({ ...prev, tags: e.target.value }))}
                  placeholder={safeT('attachments.tagsPlaceholder', 'Comma separated')}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                      : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  modalStateRef.current.urlModalOpen = false;
                  triggerRerender();
                }}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="text-xs sm:text-sm">{safeT('common.cancel', 'Cancel')}</span>
              </button>
              <button
                type="button"
                onClick={handleUrlSubmit}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700"
              >
                <LinkIcon className="h-4 w-4" />
                <span className="text-xs sm:text-sm">{safeT('attachments.addLink', 'Add Link')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editingAttachment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-lg rounded-2xl border ${cardBackground} p-6 shadow-xl`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-lg font-semibold ${textPrimary}`}>
                <span className="text-xs sm:text-sm md:text-base">{safeT('attachments.editAttachment', 'Edit Attachment')}</span>
              </h2>
              <button
                type="button"
                onClick={() => {
                  modalStateRef.current.editModalOpen = false;
                  triggerRerender();
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.title', 'Title')}</span></label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.description', 'Description')}</span></label>
                <textarea
                  rows={3}
                  value={editForm.description}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium"><span className="text-xs sm:text-sm">{safeT('attachments.tags', 'Tags')}</span></label>
                <input
                  type="text"
                  value={editForm.tags}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, tags: e.target.value }))}
                  placeholder={safeT('attachments.tagsPlaceholder', 'Comma separated')}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                      : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  modalStateRef.current.editModalOpen = false;
                  triggerRerender();
                }}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="text-xs sm:text-sm">{safeT('common.cancel', 'Cancel')}</span>
              </button>
              <button
                type="button"
                onClick={handleEditSave}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700"
              >
                <span className="text-xs sm:text-sm">{safeT('common.saveChanges', 'Save changes')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AttachmentService.propTypes = {
  entityType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAttachmentChange: PropTypes.func,
};

AttachmentService.defaultProps = {
  entityType: '',
  entityId: '',
  onAttachmentChange: undefined,
};

export default AttachmentService;
